import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { BarChart2, Users, KeyRound, ScanLine, ClipboardList, LogOut, ChevronLeft, ChevronRight, Menu, Star, Bell, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import ManualEntryModal from '../ui/ManualEntryModal';
import '../../styles/AdminLayout.css';

const NAV_ITEMS = {
  admin: [
    { to: '/admin/analytics', icon: <BarChart2 size={18} />, label: 'Tableau de bord' },
    { to: '/admin/members',   icon: <Users size={18} />,     label: 'Comptes Visiteurs' },
    { to: '/guard/visitors', icon: <Users size={18} />, label: 'Tous les visiteurs' },
    { to: '/staff/vips', icon: <Star size={18} />, label: 'Tous les VIPs' },
    { to: '/staff/invited', icon: <ClipboardList size={18} />, label: 'Toutes les invitations' },
    { to: '/guard',           icon: <ScanLine size={18} />,  label: 'Scanner QR' },
    { to: '/admin/users',     icon: <KeyRound size={18} />,  label: 'Comptes Admin/Staff' },
  ],
  staff: [
    { to: '/staff/invited', icon: <ClipboardList size={18} />, label: 'Liste des invités' },
    { to: '/staff/vips', icon: <Star size={18} />, label: 'VIP planifiés' },
  ],
  security: [
    { to: '/guard',        icon: <ScanLine size={18} />,     label: 'Scanner QR' },
    { to: '/staff/invited', icon: <ClipboardList size={18} />, label: 'Invités du jour' },
    { to: '/staff/vips', icon: <Star size={18} />, label: 'VIP du jour' },
  ],
  entreprise: [
    { to: '/company/invites', icon: <ClipboardList size={18} />, label: 'Mes invités' },
    { to: '/company/vips', icon: <Star size={18} />, label: 'Mes VIPs' },
  ],
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, isBellOpen, setIsBellOpen, markAllAsRead, deleteNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);

  const items = NAV_ITEMS[user?.role] || [];
  
  // Dynamically determine the page title from the current route
  const currentItem = items.find(item => location.pathname === item.to);
  const title = currentItem ? currentItem.label : 'Startup Village';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`admin-layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-icon">
            <img src="/images/logo.png" alt="Startup Village" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {!collapsed && <span className="brand-text">Startup Village</span>}
        </div>

        <nav className="sidebar-nav">
          {items.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              {!collapsed && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </div>
            {!collapsed && (
              <div className="user-details">
                <span className="user-name">{user?.name || user?.email || 'Admin'}</span>
                <span className="user-role">{user?.role}</span>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout} title="Déconnexion">
            <LogOut size={18} />
          </button>
        </div>

        <button className="collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Main content */}
      <main className="admin-main">
        <header className="admin-topbar">
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          <h1 className="page-title">{title}</h1>
          <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            
            {/* Manual Entry Button for Security */}
            {user?.role === 'security' && (
              <button 
                className="btn btn-primary" 
                onClick={() => setIsManualModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', fontSize: '0.875rem' }}
              >
                <ClipboardList size={16} />
                Entrée Manuelle
              </button>
            )}

            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button 
                onClick={() => { setIsBellOpen(!isBellOpen); if (!isBellOpen) markAllAsRead(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}
              >
                <Bell size={20} color="#64748b" />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '-5px', right: '-5px',
                    background: '#ef4444', color: 'white', fontSize: '10px',
                    fontWeight: 'bold', width: '16px', height: '16px',
                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {isBellOpen && (
                <div style={{
                  position: 'absolute', top: '35px', right: '0',
                  width: '320px', background: 'white', borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0',
                  zIndex: 999, overflow: 'hidden'
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', fontSize: '14px' }}>
                    Notifications
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>Aucune notification</div>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} style={{
                          padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
                          background: n.isVip ? '#fefce8' : 'white',
                          position: 'relative'
                        }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                            style={{
                              position: 'absolute', top: '12px', right: '16px',
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#cbd5e1', padding: '0'
                            }}
                            title="Supprimer"
                          >
                            <X size={14} />
                          </button>
                          <strong style={{ display: 'block', fontSize: '13px', color: '#1e293b', paddingRight: '20px' }}>
                            {n.isVip ? <><Star size={14} style={{marginRight: '4px', verticalAlign: 'text-bottom'}} /> VIP Arrivé</> : n.title}
                          </strong>
                          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#475569', paddingRight: '20px' }}>{n.message}</p>
                          <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                            {n.time.toLocaleTimeString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <span className="topbar-role-badge">{user?.role}</span>
          </div>
        </header>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>

      {/* Modals */}
      <ManualEntryModal 
        isOpen={isManualModalOpen} 
        onClose={() => setIsManualModalOpen(false)} 
      />
    </div>
  );
}
